import { apiRequest } from '../lib/httpClient'
import type { McpFirmProfile, McpFirmProfileState, McpXpmPrefill } from '@/types/mcp'
import { asArray, pickNumber, pickString } from './shared/coercion'

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
}

const strings = (raw: unknown) => asArray<unknown>(raw).map(pickString).filter(Boolean)
const optionalNumber = (raw: unknown) => (typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined)

function toProfile(raw: unknown): McpFirmProfile {
  const record = asRecord(raw)
  const people = asRecord(record['people'])
  return {
    firmName: pickString(record['firmName']),
    financialYear: pickString(record['financialYear']),
    size: pickString(record['size']),
    people: {
      practiceManager: pickString(people['practiceManager']),
      teams: asArray<unknown>(people['teams']).map((team) => {
        const t = asRecord(team)
        return { name: pickString(t['name']), lead: pickString(t['lead']) }
      }),
      keyPeople: asArray<unknown>(people['keyPeople']).map((person) => {
        const p = asRecord(person)
        return { name: pickString(p['name']), role: pickString(p['role']) }
      }),
    },
    jobNaming: pickString(record['jobNaming']),
    workflowNotes: strings(record['workflowNotes']),
    reportingNotes: strings(record['reportingNotes']),
    objectives: asArray<unknown>(record['objectives']).map((item) => {
      const o = asRecord(item)
      return { objective: pickString(o['objective']), initiatives: strings(o['initiatives']) }
    }),
    notes: strings(record['notes']),
  }
}

function toPrefill(raw: unknown): McpXpmPrefill {
  const record = asRecord(raw)
  return {
    firmName: pickString(record['firmName']),
    partners: strings(record['partners']),
    clientManagers: asArray<unknown>(record['clientManagers']).map((manager) => {
      const m = asRecord(manager)
      return { name: pickString(m['name']), clients: pickNumber(m['clients']) }
    }),
    // Older API builds send 0, rather than nothing, when XPM has no staff to count.
    staff: optionalNumber(record['staff']) || null,
    financialYearEnd: pickString(record['financialYearEnd']),
  }
}

function toState(raw: unknown): McpFirmProfileState {
  const record = asRecord(raw)
  return {
    status: pickString(record['status']).toLowerCase() === 'complete' ? 'complete' : 'not_set_up',
    updatedAt: pickString(record['updatedAt']) || null,
    updatedBy: pickString(record['updatedBy']),
    profile: toProfile(record['profile']),
    prefillFromXpm: toPrefill(record['prefillFromXpm']),
  }
}

export async function fetchMcpFirmProfile(clientId: string): Promise<McpFirmProfileState> {
  return toState(await apiRequest<unknown>('Mcp/firmProfile', { method: 'GET', query: { clientId } }))
}

/** Replaces the whole profile, so `profile` must carry every part, not just what changed. */
export async function saveMcpFirmProfile(clientId: string, profile: McpFirmProfile): Promise<McpFirmProfileState> {
  return toState(await apiRequest<unknown>('Mcp/firmProfile', { method: 'PUT', body: { clientId, profile } }))
}
